// src/lib/wpr-assembly.js
//
// WPR-SNAPSHOT — shared pure assembly functions for the Weekly Portfolio Review.
//
// This module is importable by both the Node.js server (for snapshot assembly)
// and the Vite/React client (via re-export through wprFilters.js).
//
// Architectural decision: this lib has NO React/DOM dependencies so it can run
// in either environment. Pure JS only. All display-layer strings (milestone cell,
// subtext, RAG colour) are computed here so callers are pure presentation.
//
// Exports:
//   buildWprRows, buildWprReadOutProps, fmtMilestoneCell, fmtMilestoneDate,
//   siteHasReadOutReason, projectRagRank, locationRagRank, nextMilestonePerProject

import { selectNextMilestone } from "./projectDisplaySignals.js";
import {
  T,
  PROJECT_STATUS,
  PROJECT_TYPE,
  PROJECT_PHASE,
  STAGES,
  DEFAULT_EXCLUDED_STAGES,
} from "../app/tokens.js";

// ─── nextMilestonePerProject ──────────────────────────────────────────────────
//
// Shared date-only selection has no React, transport or logger dependencies.

/**
 * Given a flat list of milestones (across projects), return a Map of
 * projectId → earliest non-complete milestone.
 *
 * @param {Array} milestones  array of milestone objects (camelCase)
 * @returns {Map<string, object>}  projectId → milestone
 */
export function nextMilestonePerProject(milestones) {
  if (!Array.isArray(milestones)) return new Map();
  const map = new Map();
  for (const id of new Set(milestones.filter(m => m?.projectId).map(m => m.projectId))) {
    const milestone = selectNextMilestone(milestones.filter(m => m?.projectId === id));
    if (milestone) map.set(id, milestone);
  }
  return map;
}

// ─── Stage sets ──────────────────────────────────────────────────────────────

// OFFICE-STAGE-REMOVE-NEGOTIATING-PROSPECTING: PIPELINE_STAGES is now empty
// (negotiating/prospecting moved to DEFAULT_EXCLUDED_STAGES).  The spread here
// still works but the distinction is gone — EXCLUDED_LOCATION_STAGES now comes
// entirely from DEFAULT_EXCLUDED_STAGES.
const EXCLUDED_LOCATION_STAGES = new Set([
  ...DEFAULT_EXCLUDED_STAGES,
]);

const STEADY_STATE_STAGE = "operational";

// ─── RAG rank ────────────────────────────────────────────────────────────────

const RAG_RANK = {
  critical: 0,
  red:      0,
  at_risk:  1,
  yellow:   1,
  on_track: 2,
  green:    2,
};

/** Sort rank for a project's statusOverall value. */
export function projectRagRank(p) {
  return RAG_RANK[p.statusOverall] ?? 3;
}

/** Sort rank for a location's health value. */
export function locationRagRank(loc) {
  return RAG_RANK[loc.health] ?? 3;
}

// ─── Milestone helpers ───────────────────────────────────────────────────────

/**
 * Format a milestone dueDate for display.
 *
 * PostgreSQL `date` columns are serialised by node-postgres as ISO timestamps
 * ("2026-06-02T00:00:00.000Z"). Two traps exist:
 *
 *   1. The shared `parseDate` appends "T00:00:00" to bare YYYY-MM-DD strings,
 *      which corrupts an already-complete ISO timestamp → Invalid Date.
 *   2. `new Date(isoString)` creates a UTC-midnight instant. In any timezone
 *      behind UTC, `toLocaleDateString` then rolls back one day.
 *
 * Fix: extract only the YYYY-MM-DD calendar portion (the string segment
 * before "T") and construct via `new Date(y, m-1, d)` — a local-midnight
 * Date object.
 *
 * Returns null for missing/invalid input so callers can omit the date prefix.
 *
 * See memory: wpr-date-serialisation.md
 */
export function fmtMilestoneDate(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const datePart = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
  const [y, m, d] = datePart.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  });
}

/**
 * Build the Next Milestone cell string.
 *
 *   Milestone with valid date  →  "Jun 2, 2026 — Design Development complete"
 *   Milestone with no date     →  "Design Development complete"
 *   No milestone               →  "—"
 */
export function fmtMilestoneCell(ms) {
  if (!ms) return "—";
  const datePart = fmtMilestoneDate(ms.dueDate);
  const name = ms.name || "—";
  return datePart ? `${datePart} - ${name}` : name;
}

// ─── Site membership ─────────────────────────────────────────────────────────

/**
 * Returns true when a location has a read-out reason and should appear as a
 * site row in the WPR roll-up.
 *
 * INCLUDE when any of:
 *   (a) health is yellow or red
 *   (b) stage is not the steady-state "operational" stage
 *   (c) a non-empty WPR editorial update already exists for this location
 *
 * EXCLUDE when:
 *   - stage is always-excluded (closed / vacated / pipeline stages)
 *   - location already shown via an active project (de-dup)
 *   - none of (a)/(b)/(c) apply
 *
 * @param {object} loc
 * @param {Set}    activeLocIds  location IDs already covered by an active project
 * @param {Map}    updatesMap    entityId → update text
 */
export function siteHasReadOutReason(loc, activeLocIds, updatesMap) {
  if (EXCLUDED_LOCATION_STAGES.has(loc.stage)) return false;
  if (activeLocIds.has(loc.id))               return false;

  if (loc.health === "yellow" || loc.health === "red") return true;
  if (loc.stage !== STEADY_STATE_STAGE)                return true;

  const update = updatesMap instanceof Map ? updatesMap.get(loc.id) : null;
  if (update && update.trim()) return true;

  return false;
}

// ─── Row assembly ─────────────────────────────────────────────────────────────

/**
 * Build the merged, sorted roll-up rows for the WPR report.
 *
 * Sort order:
 *   RAG rank ascending (critical/red → at_risk/amber → on_track/green → unknown)
 *   then alphabetical by display name within each tier.
 *
 * @param {object[]} projects   from getProjects()
 * @param {object[]} locations  from getLocations()
 * @param {object[]} milestones from getAllNextMilestones()
 * @param {Map}      updatesMap entityId → updateText
 * @returns {object[]} sorted rows with precomputed display fields
 */
export function buildWprRows(projects, locations, milestones, updatesMap) {
  const safeProjects   = Array.isArray(projects)   ? projects   : [];
  const safeLocations  = Array.isArray(locations)  ? locations  : [];
  const safeMilestones = Array.isArray(milestones) ? milestones : [];
  const safeUpdates    = updatesMap instanceof Map  ? updatesMap : new Map();

  const msMap = nextMilestonePerProject(safeMilestones);

  const activeProjects = safeProjects;
  const activeLocIds   = new Set(
    activeProjects.map((p) => p.locationId).filter(Boolean),
  );

  const projectRows = activeProjects.map((p) => ({
    entityType: "project",
    entityId:   p.id,
    rank:       projectRagRank(p),
    ragColor:   PROJECT_STATUS[p.statusOverall]?.color ?? T.mid,
    name:       p.name,
    subtext: [
      PROJECT_TYPE[p.projectType]?.label  || p.projectType  || null,
      PROJECT_PHASE[p.phase]?.label       || p.phase        || null,
    ].filter(Boolean).join(" · "),
    milestone: fmtMilestoneCell(msMap.get(p.id)),
    owner:     p.internalOwner || "—",
  }));

  const siteRows = safeLocations
    .filter((loc) => siteHasReadOutReason(loc, activeLocIds, safeUpdates))
    .map((loc) => {
      const stageLbl = STAGES[loc.stage]?.label || loc.stage || "—";
      const subParts = [loc.locationAlias, loc.region, stageLbl].filter(Boolean);
      return {
        entityType: "location",
        entityId:   loc.id,
        rank:       locationRagRank(loc),
        ragColor:   loc.health === "red"    ? T.red
                  : loc.health === "yellow" ? T.amber
                  : loc.health === "green"  ? T.green
                  : T.mid,
        name:       loc.market,
        subtext:    subParts.join(" · "),
        milestone:  "—",
        owner:      "—",
      };
    });

  return [...projectRows, ...siteRows].sort((a, b) => {
    const rankDiff = a.rank - b.rank;
    if (rankDiff !== 0) return rankDiff;
    return (a.name || "").localeCompare(b.name || "");
  });
}

// ─── Read-out props assembly ──────────────────────────────────────────────────

/**
 * Build per-project display props for the WPR "Project Read-outs" section.
 *
 * Returns one entry per ACTIVE project (phase !== "complete"), sorted
 * by RAG rank ascending then alphabetically.
 *
 * @param {object[]} projects   from getProjects()
 * @param {object[]} milestones from getAllNextMilestones()
 * @returns {object[]} sorted read-out props for each active project
 */
export function buildWprReadOutProps(projects, milestones) {
  const safeProjects   = Array.isArray(projects)   ? projects   : [];
  const safeMilestones = Array.isArray(milestones) ? milestones : [];

  const msMap          = nextMilestonePerProject(safeMilestones);
  const activeProjects = safeProjects;

  return activeProjects
    .map((p) => {
      const statusCfg = PROJECT_STATUS[p.statusOverall];
      return {
        projectId:     p.id,
        name:          p.name,
        internalOwner: p.internalOwner || "—",
        rank:          projectRagRank(p),
        ragColor:      statusCfg?.color ?? T.mid,
        statusLabel:   statusCfg?.label ?? (p.statusOverall || "—"),
        statusBg:      statusCfg?.bg    ?? T.lightGray,
        milestone:     fmtMilestoneCell(msMap.get(p.id)),
        subtext: [
          PROJECT_TYPE[p.projectType]?.label  || p.projectType  || null,
          PROJECT_PHASE[p.phase]?.label       || p.phase        || null,
        ].filter(Boolean).join(" · "),
      };
    })
    .sort((a, b) => {
      const rankDiff = a.rank - b.rank;
      if (rankDiff !== 0) return rankDiff;
      return (a.name || "").localeCompare(b.name || "");
    });
}
